import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { TPurpose } from 'common/types'
import { ALL_PRODUCTS } from 'contexts/app-context/types/app-context/components/manange-notifications'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { Routes } from 'common/constants'
import { useRequestConfirmationBottomSheetContextUS } from 'contexts/bottomsheet-context/RequestConfirmationBottomSheetContextUS'
import { useAppContext } from 'contexts/app-context/hooks'
import { TCommunications, TCommunicationType, TUserType } from 'contexts/app-context/types'

import { useMeansOfCommunication } from '../../../../product/components/means-of-communication/hooks/useMeansOfCommunication'
import { MeansOfCommunicationAdapter } from '../MeansOfCommunication.types'

interface UnitedStatesMeansOfCommunicationAdapterParams {
  communications: TCommunications<TUserType>
}

type UnitedStatesNotifications = TPurpose<TUserType>[string]['promotionsAndNewsletterNotifications']

const areActiveMethodsEqual = (
  currentMethods: Record<string, boolean>,
  nextMethods: Record<string, boolean>,
) => {
  const currentMethodIds = Object.keys(currentMethods)
  const nextMethodIds = Object.keys(nextMethods)

  return (
    currentMethodIds.length === nextMethodIds.length &&
    currentMethodIds.every((methodId) => currentMethods[methodId] === nextMethods[methodId])
  )
}

const normalizeActiveMethods = (notifications: UnitedStatesNotifications) => {
  const activeMethods = Object.entries(notifications).reduce<Record<string, boolean>>(
    (methods, [methodId, isActive]) => {
      if (methodId !== ALL_PRODUCTS) methods[methodId] = isActive

      return methods
    },
    {},
  )

  activeMethods[ALL_PRODUCTS] = !Object.entries(activeMethods).some(
    ([methodId, isActive]) => methodId !== ALL_PRODUCTS && isActive,
  )

  return activeMethods
}

export const useUnitedStatesMeansOfCommunicationAdapter = ({
  communications,
}: UnitedStatesMeansOfCommunicationAdapterParams): MeansOfCommunicationAdapter => {
  const { userSelectedEntity } = useAppContext()
  const { filteredCommunications, isPurposesError, isPurposesLoading, productId, purpose } =
    useMeansOfCommunication({ communications })
  const { mutation, setChangingPurpose, setShowDialog, setShowSuccessStep } =
    useRequestConfirmationBottomSheetContextUS()
  const { data } = mutation
  const [activeMethods, setActiveMethods] = useState<Record<string, boolean>>({})
  const remoteActiveMethodsRef = useRef<Record<string, boolean> | undefined>(undefined)

  const localPurposes = useMemo(() => {
    if (!purpose) return {}
    if (!data) return purpose

    return { ...purpose, ...data.preferences } as TPurpose<TUserType>
  }, [data, purpose])

  const activePurposes = useMemo(() => {
    if (!productId || !localPurposes[productId]) return []

    return Object.entries(localPurposes[productId].promotionsAndNewsletterNotifications).filter(
      ([label, communication]) => {
        const isPhoneOrWhatsapp = label === 'PHONE' || label === 'WHATSAPP'
        return communication && !isPhoneOrWhatsapp
      },
    )
  }, [localPurposes, productId])

  const remoteActiveMethods = useMemo(() => {
    const productNotifications = productId
      ? localPurposes[productId]?.promotionsAndNewsletterNotifications
      : undefined

    return productNotifications ? normalizeActiveMethods(productNotifications) : undefined
  }, [localPurposes, productId])

  useEffect(() => {
    if (!remoteActiveMethods) return

    const hasRemotePreferencesChanged = !areActiveMethodsEqual(
      remoteActiveMethodsRef.current ?? {},
      remoteActiveMethods,
    )

    if (!hasRemotePreferencesChanged) return

    remoteActiveMethodsRef.current = remoteActiveMethods
    setActiveMethods((currentMethods) =>
      areActiveMethodsEqual(currentMethods, remoteActiveMethods)
        ? currentMethods
        : remoteActiveMethods,
    )
  }, [remoteActiveMethods])

  const handleSwitch = useCallback(
    async (id: TCommunicationType<TUserType>, isActive: boolean) => {
      if (!productId) return

      const activate = !isActive

      const action =
        id === ALL_PRODUCTS && activate
          ? 'toggleAllCommunicationNotification'
          : 'toggleCommunicationNotification'

      try {
        await sendSelectContent({
          flow: 'mean_of_communication',
          step: 'notifications_management',
          screen_name: Routes.NOTIFICATIONS_MANAGEMENT,
          element_name: action,
          element_type: 'button',
          parent_name: 'MeanOfCommunication',
          scenario: JSON.stringify({
            screen: Routes.NOTIFICATIONS_MANAGEMENT,
            product: userSelectedEntity,
            activate: `${activate}`,
            productId,
            ref_type: 'button',
            content_action: 'touch',
          }),
        })
      } catch (error: unknown) {
        logBridgeError('send mean of communication tic', error)
      }

      const isLastActiveToggle = activePurposes.length === 1
      const shouldDisableAllCommunications = id === ALL_PRODUCTS && activate
      const shouldDisableLastCommunication = isActive && isLastActiveToggle && id !== ALL_PRODUCTS
      const shouldOpenConfirmation =
        shouldDisableAllCommunications || shouldDisableLastCommunication

      if (shouldOpenConfirmation) {
        setShowSuccessStep(false)
        setChangingPurpose({
          body: {
            promotionsAndNewsletterCommunicationChannel: {
              channelType: ALL_PRODUCTS,
              enabled: true,
            },
          },
          purpose: productId,
        })
        setShowDialog(true)
        return
      }

      setActiveMethods((currentMethods) => {
        const updatedMethods = { ...currentMethods, [id]: activate }

        if (id !== ALL_PRODUCTS) {
          updatedMethods[ALL_PRODUCTS] = !Object.entries(updatedMethods).some(
            ([methodId, isMethodActive]) => methodId !== ALL_PRODUCTS && isMethodActive,
          )
        }

        return updatedMethods
      })

      mutation.mutate({
        body: {
          promotionsAndNewsletterCommunicationChannel: {
            channelType: id,
            enabled: activate,
          },
        },
        purpose: productId,
      })
    },
    [
      activePurposes,
      mutation,
      productId,
      setChangingPurpose,
      setShowDialog,
      setShowSuccessStep,
      userSelectedEntity,
    ],
  )

  if (isPurposesLoading) return { status: 'loading' }

  if (isPurposesError || purpose === undefined) return { status: 'error' }

  if (!productId) return { status: 'ready', methods: [] }

  const notifications = localPurposes[productId]?.promotionsAndNewsletterNotifications
  const isGroupDisabled = !!notifications?.DISABLE_ALL_COMMUNICATION_CHANNELS

  return {
    status: 'ready',
    methods: filteredCommunications.map((communication) => {
      const isDisableAllCommunication = communication.key === ALL_PRODUCTS
      const derivedIsActive = isDisableAllCommunication
        ? activePurposes.filter(([name]) => name !== ALL_PRODUCTS).length === 0
        : communication.key !== 'PHONE' && !!notifications?.[communication.key]
      const isActive = activeMethods[communication.key] ?? derivedIsActive

      return {
        id: communication.key,
        icon: communication.icon,
        name: communication.name,
        isActive,
        isDisabled: !isDisableAllCommunication && isGroupDisabled,
        isDontDisturb: false,
        onSwitch: () => handleSwitch(communication.key, isActive),
      }
    }),
  }
}
